import api from '../utils/api.js'
import { mockUsers } from '../data/mockCommunityData.js'

/**
 * Normalize a backend user to a consistent shape
 */
const normalizeUser = (u) => ({
  id: u._id || u.id,
  name: u.name || u.displayName || '',
  username: u.username || '',  // never fabricate — empty string means not yet assigned
  email: u.email || '',
  location: u.city || u.location || '',
  city: u.city || u.location || '',
  phone: u.phone || u.phoneNumber || '',
  followers: u.followersCount ?? (Array.isArray(u.followers) ? u.followers.length : u.followers) ?? 0,
  followersCount: u.followersCount ?? 0,
  following: u.followingCount ?? (Array.isArray(u.following) ? u.following.length : u.following) ?? 0,
  // Preserve the raw following array of IDs so the Collectors page can seed follow state
  followingIds: Array.isArray(u.following) ? u.following.map(String) : [],
  avatar: u.avatar || u.photoURL || '',
  coverImage: u.coverImage || '',
  bio: u.aboutMe || u.bio || '',
  aboutMe: u.aboutMe || u.bio || '',
  // Keep as array — components that need a string can join themselves
  collectionInterests: Array.isArray(u.collectionInterests)
    ? u.collectionInterests
    : (u.collectionInterests ? u.collectionInterests.split(',').map(s => s.trim()).filter(Boolean) : []),
  totalItems: u.totalItems || u.totalItemsAdded || 0,
  totalItemsAdded: u.totalItems || u.totalItemsAdded || 0,
  totalCollections: u.totalCollections || 0,
  joinedDate: u.joinedDate || u.createdAt || '',
  role: u.role || 'user',
  profileComplete: u.profileComplete || false,
  publicProfileEnabled: u.publicProfileEnabled !== false,
  awards: Array.isArray(u.awards) ? u.awards : [],
})

export const userService = {
  /**
   * Get top contributors
   */
  async getTopContributors(limit = 10, period = 'month') {
    try {
      const res = await api.get(`/users/top-contributors?limit=${limit}&period=${period}`)
      return res.data?.data?.contributors || []
    } catch (err) {
      console.warn('[userService] getTopContributors API unavailable:', err.message)
      return []
    }
  },

  /**
   * Get current authenticated user
   */
  async getMe() {
    const res = await api.get('/auth/me')
    return normalizeUser(res.data?.data?.user || res.data?.data)
  },

  /**
   * Verify Firebase token with backend (creates user if new)
   */
  async verifyToken(firebaseToken) {
    const res = await api.post('/auth/verify-token', {}, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
    })
    return normalizeUser(res.data?.data?.user || res.data?.data)
  },

  /**
   * Get a user by ID
   */
  async getUser(userId) {
    try {
      const res = await api.get(`/users/${userId}`)
      return normalizeUser(res.data?.data?.user || res.data?.data)
    } catch (err) {
      console.warn('[userService] getUser API unavailable, using mock data:', err.message)
      const mock = mockUsers.find((u) => String(u.id) === String(userId))
      return mock ? normalizeUser(mock) : null
    }
  },

  /**
   * Get a user by username (for public profiles)
   */
  async getUserByUsername(username) {
    const res = await api.get(`/users/by-username/${username}`)
    return normalizeUser(res.data?.data?.user || res.data?.data)
  },

  /**
   * Get all users (discovery) — supports ?search=, ?interests=, ?limit=, ?skip=
   * Returns { users: [], total: number } or falls back to mock array
   */
  async getUsers(params = {}) {
    try {
      const res = await api.get('/users', { params })
      const data = res.data?.data
      // Backend returns { users: [], total, limit, skip }
      if (data && Array.isArray(data.users)) {
        return { users: data.users.map(normalizeUser), total: data.total || data.users.length }
      }
      // Fallback: plain array
      const items = Array.isArray(data) ? data : []
      return { users: items.map(normalizeUser), total: items.length }
    } catch (err) {
      console.warn('[userService] getUsers API unavailable, using mock data:', err.message)
      const filtered = mockUsers.map(normalizeUser)
      return { users: filtered, total: filtered.length }
    }
  },

  /**
   * Update current user profile
   */
  async updateProfile(data) {
    const res = await api.put('/users/profile', data)
    return normalizeUser(res.data?.data?.user || res.data?.data)
  },

  /**
   * Complete onboarding profile
   */
  async completeProfile(data) {
    const res = await api.post('/users/complete-profile', data)
    return normalizeUser(res.data?.data?.user || res.data?.data)
  },

  /**
   * Follow a user
   */
  async followUser(userId) {
    const res = await api.post(`/users/${userId}/follow`)
    return res.data
  },

  /**
   * Unfollow a user
   */
  async unfollowUser(userId) {
    const res = await api.delete(`/users/${userId}/follow`)
    return res.data
  },
}

export default userService
